import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import TikTokIcon from "@/components/icons/TikTokIcon";
import { BRAND } from "@/data/founder";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/booking", label: "Booking" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`cc-nav fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "py-2" : "py-3"
      }`}
    >
      <div className="cc-container flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-brand"
          aria-label="Calm & Connected — Home"
          className="group flex items-center"
        >
          <img
            src={BRAND.logo}
            alt="Calm & Connected"
            className={`block transition-all duration-500 rounded-2xl shadow-[0_6px_20px_rgba(0,0,0,0.08)] ${
              scrolled ? "h-20 sm:h-24 md:h-24" : "h-28 sm:h-32 md:h-32"
            }`}
            data-testid="nav-logo"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors duration-300 ${
                  isActive
                    ? "text-[var(--cc-primary)]"
                    : "text-[var(--cc-text-soft)] hover:text-[var(--cc-text)]"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/booking"
            data-testid="nav-cta"
            className="cc-btn-primary text-sm py-2 px-5"
          >
            Connect With Me
          </Link>
          <a
            href={BRAND.socials.tiktok}
            target="_blank"
            rel="noreferrer"
            aria-label="TikTok — @camilieyes"
            data-testid="nav-tiktok"
            className="w-10 h-10 rounded-full border border-[var(--cc-line)] flex items-center justify-center text-[var(--cc-text-soft)] hover:text-[var(--cc-primary)] hover:border-[var(--cc-primary)]/40 transition-colors"
          >
            <TikTokIcon size={16} />
          </a>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((s) => !s)}
          className="md:hidden p-2 rounded-full text-[var(--cc-text)]"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden cc-container mt-4 pb-4">
          <div className="cc-card p-5 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-2 px-3 rounded-xl text-base transition-colors ${
                    isActive
                      ? "text-[var(--cc-primary)] bg-[var(--cc-surface)]"
                      : "text-[var(--cc-text-soft)] hover:bg-[var(--cc-surface)]"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/booking"
              data-testid="nav-mobile-cta"
              className="cc-btn-primary mt-2 justify-center"
            >
              Connect With Me
            </Link>
            <a
              href={BRAND.socials.tiktok}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-mobile-tiktok"
              className="mt-3 inline-flex items-center justify-center gap-2 py-2 px-4 rounded-full border border-[var(--cc-line)] text-sm text-[var(--cc-text-soft)] hover:text-[var(--cc-primary)] hover:border-[var(--cc-primary)]/40 transition-colors"
            >
              <TikTokIcon size={15} />
              TikTok · @camilieyes
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
